// Service Worker Manager for Audio Caching
// Manages registration and communication with the audio cache service worker

class ServiceWorkerManager {
  constructor() {
    this.registration = null
    this.isSupported = 'serviceWorker' in navigator
    this.isRegistered = false
  }

  /**
   * Register the audio cache service worker
   */
  async register() {
    if (!this.isSupported) {
      return false
    }

    try {
      this.registration = await navigator.serviceWorker.register(
        '/audio-service-worker.js',
        { scope: '/' }
      )

      this.isRegistered = true

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready

      return true
    } catch (_error) {
      return false
    }
  }

  /**
   * Unregister the service worker
   */
  async unregister() {
    if (!this.registration) {
      return false
    }

    try {
      const success = await this.registration.unregister()
      this.isRegistered = false
      return success
    } catch (_error) {
      return false
    }
  }

  /**
   * Send message to service worker
   */
  async sendMessage(type, data = {}) {
    if (!this.isRegistered) {
      throw new Error('Service Worker not registered')
    }

    const messageChannel = new MessageChannel()

    return new Promise((resolve, reject) => {
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error))
        } else {
          resolve(event.data)
        }
      }

      const controller = navigator.serviceWorker.controller
      if (!controller) {
        reject(new Error('No active service worker controller'))
        return
      }

      controller.postMessage({ type, data }, [messageChannel.port2])
    })
  }

  /**
   * Cache audio files via service worker
   */
  async cacheAudioFiles(urls, onProgress) {
    if (!Array.isArray(urls) || urls.length === 0) {
      return { success: true, cached: 0 }
    }

    // Cache in batches to avoid overwhelming the SW
    const BATCH_SIZE = 20
    const batches = []

    for (let i = 0; i < urls.length; i += BATCH_SIZE) {
      batches.push(urls.slice(i, i + BATCH_SIZE))
    }

    let totalCached = 0

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i]
      const result = await this.sendMessage('CACHE_AUDIO_FILES', {
        urls: batch
      })
      totalCached += result.cached || 0

      if (onProgress) {
        onProgress(totalCached, urls.length)
      }
    }

    return { success: true, cached: totalCached }
  }

  /**
   * Clear audio cache
   */
  async clearCache() {
    const result = await this.sendMessage('CLEAR_AUDIO_CACHE')
    return result
  }

  /**
   * Clear specific mission audio files from cache (Safari fix)
   * @param {string[]} urls - Array of audio URLs to clear
   */
  async clearMissionCache(urls) {
    if (!Array.isArray(urls) || urls.length === 0) {
      return { success: true, cleared: 0 }
    }

    try {
      const result = await this.sendMessage('CLEAR_MISSION_AUDIO_CACHE', {
        urls
      })
      return result
    } catch (error) {
      console.error('Failed to clear mission cache:', error)
      return { success: false, cleared: 0 }
    }
  }

  /**
   * Get cache size
   */
  async getCacheSize() {
    try {
      const result = await this.sendMessage('GET_CACHE_SIZE')
      return result.size || 0
    } catch (_error) {
      return 0
    }
  }

  /**
   * Check if URL is cached
   */
  async isCached(url) {
    try {
      const result = await this.sendMessage('CHECK_CACHED', { url })
      return result.isCached || false
    } catch (_error) {
      return false
    }
  }

  /**
   * Get service worker status
   */
  getStatus() {
    return {
      supported: this.isSupported,
      registered: this.isRegistered,
      active: !!navigator.serviceWorker.controller
    }
  }
}

// Create singleton instance
const serviceWorkerManager = new ServiceWorkerManager()

export default serviceWorkerManager
